import { AfterViewInit, Component, HostBinding, Input, QueryList, Renderer2, ViewChild, ViewChildren, ViewEncapsulation } from '@angular/core';
import { NgForm } from '@angular/forms';
import * as _ from 'underscore';
import { Endpoint, HelpIndex, SettingsType } from '../../../../models/enums';
import { FileGroup, FileGroupChild } from '../../../../models/file-group';
import { GenericColDef, GridRowSelectionType } from '../../../../models/grid-context';
import { ActionType, ObjectActionListType, Permissions } from '../../../../models/permission';
import { SettingsNavigationContentComponent } from '../../../../models/settings-navigation-content-component';
import { CommonService } from '../../../../services/common/common.service';
import { RestService } from '../../../../services/rest/rest.service';
import { FilterComponent } from '../../../filter/filter.component';
import { GenericGridComponent } from '../../../generic-grid/generic-grid.component';
import { GenericTreeNode } from '../../../generic-tree/generic-tree-node';
import { DragCellComponent } from '../../../grid-templates/drag-cell/drag-cell.component';
import { isNullOrUndefined } from '../../../../models/utils';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { DragDropData } from '../../../../models/drag-drop-data';

@Component({
    standalone: false,
    selector: 'res-billing-report',
    templateUrl: './billing-report.component.html',
    styleUrls: ['./billing-report.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class BillingReportComponent implements SettingsNavigationContentComponent {
    @HostBinding('class') classes = 'maximized-component';
    @ViewChildren(GenericGridComponent) genericGrids: QueryList<GenericGridComponent>;
    @ViewChild('contentForm', { static: true }) contentForm: NgForm;
    @ViewChild('filterComponent') filter: FilterComponent;

    @Input() automationId = 'settings-modal-billing-reports';

    isBusyMessage: string;
    helpIndex: HelpIndex;
    handle: string;
    isProject: boolean;
    isBusy = false;
    allFileTypes: Array<any>;
    supportedFileTypesFields = new Array<any>();
    excludedFileTypesFields = new Array<FileGroup>();
    originalExcludedFileTypesFields: Array<string>;
    originalExcludedFileTypesFieldsChildren: Map<string, Array<string>>;
    children = new Array<FileGroupChild>();
    numConcurrentRequests = 0;
    filteredFiles = [];
    fieldsToSend: any;
    profileHandle: string;
    hasPermissions = true;
    validChanges = true;
    parsingLibraryVersion: string;
    isSystemUser = false;
    draggedItem = null;

    @Input() queriesChanged: () => void;


    get isDirty(): boolean {
        if (this.contentForm) {
            return this.contentForm.dirty;
        }
        return false;
    }
    set isDirty(value: boolean) {
        if (this.contentForm && this.contentForm.control) {
            if (value) {
                this.contentForm.control.markAsDirty();
            } else {
                this.contentForm.control.markAsPristine();
            }
        }
    }

    supportedFileTypes = GridRowSelectionType.single;
    supportedFileColumns = new Array<GenericColDef>(
        {
            headerName: '',
            field: 'name',
            automationId: this.automationId,
            cellRenderer: DragCellComponent,
            cellRendererParams: {
                dragScope: ['availableField'],
                inlineEdit: () => false,
                required: () => false,
                setDraggedItem: (param) => this.draggedItem = param
            },
        }
    );

    rowDisableRules = {
        'disabled-row': () => {
            return !this.hasPermissions;
        },
        'disabled-cell': () => {
            return !this.hasPermissions;
        }
    };

    constructor(private commonService: CommonService, private restService: RestService, private renderer: Renderer2) { }

    selectedTreeItemChanged(treeItem: GenericTreeNode, projectHandle?: string, settingsType?: SettingsType): void {
        this.handle = projectHandle ? projectHandle : treeItem.data.handle;
        this.isProject = projectHandle !== null;
        this.isDirty = false;
        this.filteredFiles = [];
        this.helpIndex = this.isProject ? HelpIndex.case_billing_reports_manage_dlg : (settingsType === SettingsType.system ? HelpIndex.sys_template_billing_dlg : HelpIndex.org_billing_reports_template_dlg);
        this.isSystemUser = (settingsType === SettingsType.system && (this.commonService.currentUser?.originalCustomerName === 'super_system_customer' || this.commonService.currentUser.importedFromSystemUser));
        const permissionType = projectHandle ? ObjectActionListType.PRJ_BILLING_REPORTS : this.isSystemUser ? ObjectActionListType.SYSTEM_BILLING_REPORTS : ObjectActionListType.ORG_BILLING_REPORTS;
        this.hasPermissions = Permissions.checkUserPermissions([{ permissionType: permissionType, permissionActions: [ActionType.CREATE] }]);
        this.loadData();
        if (!isNullOrUndefined(this.filter)) {
            this.filter.clear();
        }
    }

    loadData(): void {
        this.isBusyMessage = 'Loading Billing Reports...';
        this.isBusy = true;
        this.supportedFileTypesFields = new Array<any>();

        this.restService.post(Endpoint.projectManager_listBillingReportSettings, { handle: this.handle, requestHandle: this.handle, systemScope: this.isSystemUser }).then(responseBillingSettings => {
            if (responseBillingSettings && responseBillingSettings.profiles && responseBillingSettings.requestHandle === this.handle) {
                this.profileHandle = responseBillingSettings.profiles[0].handle;

                this.excludedFileTypesFields = [];
                for (let i = 0; i < responseBillingSettings.profiles[0].entries.length; i++) {
                    const group = this.isGroupCreated(this.excludedFileTypesFields, responseBillingSettings.profiles[0].entries[i]);
                    const groupItems = _.filter(responseBillingSettings.profiles[0].entries, (child) => child.categoryName === responseBillingSettings.profiles[0].entries[i].categoryName);

                    if (!group) {
                        const item = new FileGroup({
                            externalName: responseBillingSettings.profiles[0].entries[i].categoryName,
                            children: [],
                        });

                        this.excludedFileTypesFields.push(item);
                        this.addChildren(this.excludedFileTypesFields[this.excludedFileTypesFields.length - 1], groupItems);
                    }
                }
                this.excludedFileTypesFields = _.sortBy(this.excludedFileTypesFields, function (i) { return i.externalName.toLowerCase(); });
                this.initializeOriginalExcludedFileTypesFields();
                const parsingLibraryName = responseBillingSettings.profiles[0].externalParsingLibrary;
                this.loadFileTypesForParsingLibrary(parsingLibraryName);
                this.parsingLibraryVersion = parsingLibraryName === 'OIT' ? 'V1' : 'V2'

            } else {
                this.isBusy = false;
            }
        }, error => {
            this.isBusy = false;
        });

    }

    loadFileTypesForParsingLibrary(parsingLibraryVersion: string): void {
        this.restService.post(Endpoint.realmManager_getAllFileTypes, {
            filters: [{
                attribute: 'version',
                operator: 'EQUALS',
                value: parsingLibraryVersion
            }],
            systemScope: this.isSystemUser
        }).then(responseAllFields => {
            this.isBusy = false;
            if (responseAllFields && responseAllFields.allFileTypes) {
                _.each(responseAllFields.allFileTypes, (element) => {
                    this.supportedFileTypesFields = this.supportedFileTypesFields.concat(element);
                });
                _.each(this.supportedFileTypesFields, (element, index, list) => {
                    list[index] = { name: element };
                });

                this.genericGrids.first.setData(this.filteredFiles.length > 0 ? this.filteredFiles : this.supportedFileTypesFields);
                if (!isNullOrUndefined(this.filter)) {
                    this.filter.clear();
                }
            }
        }, () => {
            this.isBusy = false;
        });
    }

    addChildren(parent, children): void {
        children.forEach(child => {
            parent.children.push(new FileGroupChild({
                name: child.name,
                parent: parent
            }));
        });
    }

    isGroupCreated(array, itemToSearch): any {
        const groupCreated = _.find(array, (item) => item.externalName === itemToSearch.categoryName);
        return groupCreated;
    }

    filterData(str: string): void {
        if (str) {
            this.filteredFiles = this.supportedFileTypesFields.filter(value => {
                return value.name.toLowerCase().includes(str);
            });
        } else {
            this.filteredFiles = this.supportedFileTypesFields;
        }
        this.genericGrids.first.setData(this.filteredFiles);
    }

    newGroupField(): void {
        this.isDirty = true;
        this.excludedFileTypesFields.unshift(new FileGroup({
            externalName: this.getNewFieldName(),
            children: []
        }));
    }

    private getNewFieldName(): string {
        let index = 1;
        const prefix = 'NewGroup';
        while (_.find(this.excludedFileTypesFields, (item: FileGroup) => prefix + index === item.externalName)) {
            index++;
        }
        return prefix + index;
    }

    initializeOriginalExcludedFileTypesFields(): void {

        this.originalExcludedFileTypesFields = new Array<string>();
        this.originalExcludedFileTypesFieldsChildren = new Map<string, Array<string>>();

        for (let i = 0; i < this.excludedFileTypesFields.length; i++) {

            // construct a map of field to its children
            const children = new Array<string>();
            if (this.excludedFileTypesFields[i].children.length > 0) {
                this.excludedFileTypesFields[i].children.forEach(child => {
                    children.push(child.name);
                });
                this.originalExcludedFileTypesFieldsChildren.set(this.excludedFileTypesFields[i].externalName, children);
            }

            // add the field name
            this.originalExcludedFileTypesFields.push(this.excludedFileTypesFields[i].externalName);
        }
    }

    trackByFn(index) {
        return index;
    }

    onRemoveRow(index): void {
        this.isDirty = false;
        this.excludedFileTypesFields.splice(index, 1);
        this.queryChanged();
        this.isDirty = this.dirty();
    }

    onRemoveChild(index, entry: FileGroup): void {
        this.isDirty = false;
        entry.children.splice(index, 1);
        this.queryChanged();
        this.isDirty = this.dirty();
    }

    queryChanged(): void {
        this.isDirty = true;
        if (this.queriesChanged) { this.queriesChanged(); }
    }

    isDragEnabled = () => {
        if (this.hasPermissions) {
            if (this.draggedItem) {
                for (const field of this.excludedFileTypesFields) {
                    if (field.children.find(c => c.name === this.draggedItem.name)) {
                        return false;
                    }
                }
            }
            return true;
        }
        return false;
    }

    onFieldDrop(event: any, entry: FileGroup, child?: FileGroupChild): void {
        if (event?.data && entry) {
            this.isDirty = true;
            if (event.data) {
                if (event.data.parent) {
                    const indexOld = event.data.parent.children.indexOf(event.data);
                    event.data.parent.children.splice(indexOld, 1);
                }
                if (child) {
                    const index = entry.children.indexOf(child);
                    entry.children.splice(index, 0, new FileGroupChild({
                        name: event.data.name,
                        parent: entry
                    }));
                    if (event.nativeEvent && event.nativeEvent.currentTarget && event.nativeEvent.currentTarget.parentElement) {
                        const parentElement = event.nativeEvent.currentTarget.parentElement.parentElement;
                        if (parentElement) {
                            parentElement.className = parentElement.className.replace('drag-over-border', '');
                        }
                    }
                } else {
                    entry.children.push(new FileGroupChild({
                        name: event.data.name,
                        parent: entry
                    }));
                }
            }
            this.isDirty = this.dirty();
        }
    }

    drop(event: CdkDragDrop<string[]>) {
        if (event.previousContainer === event.container) {
            moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
            this.isDirty = this.dirty();
        }
    }

    saveChanges(fromAutoSave = false): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            this.isBusy = true;
            this.isBusyMessage = 'Saving Billing Report Template ...';
            const itemSelected = [];
            this.excludedFileTypesFields.forEach(file => {
                file.children.forEach(child => {
                    const item = {
                        name: child.name,
                        categoryName: file.externalName,
                    };
                    itemSelected.push(item);
                });
            });

            this.fieldsToSend = {
                ownerHandle: this.handle,
                handle: this.profileHandle,
                timestamp: new Date().toLocaleString().replace(/,\s?/g, ' '),
                entries: itemSelected
            };

            this.restService.post(Endpoint.billingReportManager_updateBillingReportSettings, { requestHandle: this.handle, profile: this.fieldsToSend, systemScope: this.isSystemUser }).then(billingResponse => {
                this.isDirty = false;
                this.isBusy = false;
                if (!isNullOrUndefined(this.filter)) {
                    this.filter.clear();
                }
                if (!fromAutoSave) {
                    this.loadData();
                }
                resolve(true);
            }, error => {
                this.isBusy = false;
                resolve(false);
            });
        });
    }

    isDisabled(): boolean {
        return (!this.isDirty || this.isBusy || !this.excludedFileTypesFields || (this.excludedFileTypesFields.length === 0));
    }

    checkState(): Promise<any> {
        if (this.isDirty) {
            return new Promise<boolean>((resolve) => {
                this.commonService.openConfirmationModal('Do you want to apply changes?', 'Apply Changes', 'OK', 'Cancel').then(result => {
                    if (result.modalResult) {
                        this.saveChanges();
                    }
                    resolve(true);
                });
            });
        } else {
            return Promise.resolve(true);
        }
    }

    discardChanges(): void {
        this.isDirty = false;
        this.loadData();
    }

    openHelp = () => this.commonService.openHelp(this.helpIndex);

    dirty(): boolean {
        let bDirty = false;
        if (this.originalExcludedFileTypesFields.length !== this.excludedFileTypesFields.length) {
            bDirty = true;
            return bDirty;
        }

        let originalChildren = [];
        for (let i = 0; i < this.originalExcludedFileTypesFields.length && !bDirty; i++) {
            if (this.originalExcludedFileTypesFields[i] !== this.excludedFileTypesFields[i].externalName) {
                bDirty = true;
                break;
            }

            if (this.originalExcludedFileTypesFieldsChildren.has(this.originalExcludedFileTypesFields[i])) {
                originalChildren = this.originalExcludedFileTypesFieldsChildren.get(this.originalExcludedFileTypesFields[i]);
            }

            if (this.excludedFileTypesFields[i].children.length !== originalChildren.length) {
                bDirty = true;
                break;
            }

            const currentChildren = new Array<string>();
            const originalChildrenString = originalChildren.join(',');
            this.excludedFileTypesFields[i].children.forEach(child => {
                currentChildren.push(child.name.trim());
            });
            const currentChildrenString = currentChildren.join(',');

            if (currentChildrenString !== originalChildrenString) {
                bDirty = true;
                break;
            }
        }

        return bDirty;
    }
}
